"use client";
import { useState, useEffect, useRef } from 'react';
import useSWR from 'swr';
import { fetcher, swrOptions } from '@/lib/swr';

// Icons
const UploadIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" x2="12" y1="3" y2="15" /></svg>;
const FileIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" /></svg>;
const CloseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>;
const CheckIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>;
const AlertIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" x2="12" y1="8" y2="12" /><line x1="12" x2="12.01" y1="16" y2="16" /></svg>;
const EyeIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></svg>;
const PlusIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="M12 5v14" /></svg>;

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 10 }, (_, i) => CURRENT_YEAR - 5 + i);

interface PayrollRecord {
    employee_id: string;
    employee_name: string;
    company?: string;
    department?: string;
    designation?: string;
    period?: string;
    period_id?: number;
    total_days?: number;
    paid_days?: number;
    weekly_holidays?: number;
    ot_arrears_days?: number;
    salary_days?: number;
    actual_basic?: number;
    earned_basic?: number;
    gross_salary?: number;
    total_deductions?: number;
    net_salary?: number;
    retention_amount?: number;
    net_pay?: number;
    [key: string]: any;
}

interface Period {
    id: number;
    month: string;
    year: number;
}

export default function PayrollPage() {
    const [selectedPeriodId, setSelectedPeriodId] = useState<string>('');
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');

    const { data: periodsData, mutate: mutatePeriods } = useSWR('/api/payroll-periods', fetcher, swrOptions);
    const { data: companiesData } = useSWR('/api/companies', fetcher, swrOptions);

    const periods: Period[] = Array.isArray(periodsData) ? periodsData : [];
    const companies: any[] = Array.isArray(companiesData) ? companiesData : [];

    // Automatically select first period if not set
    useEffect(() => {
        if (periods.length > 0 && !selectedPeriodId) {
            setSelectedPeriodId(periods[0].id.toString());
        }
    }, [periods, selectedPeriodId]);

    const payrollQueryUrl = `/api/payroll?periodId=${selectedPeriodId}${selectedCompanyId ? `&companyId=${selectedCompanyId}` : ''}`;
    const { data: rawPayrollData, mutate: mutatePayroll, isLoading: loadingPayroll } = useSWR(
        selectedPeriodId ? payrollQueryUrl : null,
        fetcher,
        swrOptions
    );

    const payrollData: PayrollRecord[] = Array.isArray(rawPayrollData) ? rawPayrollData : [];
    const loading = loadingPayroll && payrollData.length === 0;

    const [uploading, setUploading] = useState(false);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

    const [uploadResult, setUploadResult] = useState<{
        success: boolean;
        message: string;
        errors?: string[];
        successCount?: number;
    } | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadMonth, setUploadMonth] = useState(MONTHS[new Date().getMonth()]);
    const [uploadYear, setUploadYear] = useState(CURRENT_YEAR.toString());
    const [selectedEmployee, setSelectedEmployee] = useState<PayrollRecord | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const fetchPeriods = () => mutatePeriods();
    const fetchCompanies = () => {};
    const fetchPayrollData = () => mutatePayroll();


    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    };

    const handleFileSelect = (file: File) => {
        const validTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel',
            'text/csv'
        ];
        if (validTypes.includes(file.type) || file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv')) {
            setSelectedFile(file);
            setUploadResult(null);
        } else {
            setUploadResult({ success: false, message: 'Please select an Excel (.xlsx, .xls) or CSV file' });
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        setUploading(true);
        setUploadResult(null);
        setShowErrors(false);

        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('month', uploadMonth);
        formData.append('year', uploadYear);

        try {
            const res = await fetch('/api/payroll', {
                method: 'POST',
                body: formData,
            });
            const result = await res.json();

            if (res.ok) {
                const hasErrors = result.errors?.length > 0;
                setUploadResult({
                    success: !hasErrors || result.success > 0,
                    message: hasErrors
                        ? `Processed ${result.success} records. ${result.errors.length} error(s) found.`
                        : `Successfully processed ${result.success} records for ${result.period}`,
                    errors: result.errors || [],
                    successCount: result.success || 0,
                });
                setSelectedFile(null);
                fetchPeriods();
                if (result.periodId) {
                    setSelectedPeriodId(result.periodId.toString());
                }
            } else {
                setUploadResult({
                    success: false,
                    message: result.error || 'Upload failed',
                    errors: result.errors || [],
                });
            }
        } catch (error: any) {
            setUploadResult({
                success: false,
                message: error.message || 'Upload failed',
            });
        } finally {
            setUploading(false);
        }
    };

    const formatCurrency = (val: any) => {
        const num = parseFloat(val) || 0;
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(num);
    };

    // Calculate totals
    const totals = payrollData.reduce((acc, row) => ({
        grossSalary: acc.grossSalary + (Number(row.gross_salary) || 0),
        totalDeductions: acc.totalDeductions + (Number(row.total_deductions) || 0),
        netPay: acc.netPay + (Number(row.net_pay) || 0),
    }), { grossSalary: 0, totalDeductions: 0, netPay: 0 });

    return (
        <div className="p-4 md:p-8 max-w-full mx-auto min-h-full">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Payroll Management</h1>
                    <p className="text-slate-500 text-sm">View and manage employee salary records</p>
                </div>
                <button
                    onClick={() => setIsUploadModalOpen(true)}
                    className="bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-medium shadow-lg shadow-indigo-200 hover:bg-indigo-700 transition flex items-center gap-2 active:scale-95"
                >
                    <PlusIcon /> <span className="hidden md:inline">Upload Payroll Data</span> <span className="md:hidden">Upload</span>
                </button>
            </div>

            {/* Upload Modal */}
            {isUploadModalOpen && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl p-6 relative">
                        <button
                            onClick={() => setIsUploadModalOpen(false)}
                            className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition"
                        >
                            <CloseIcon />
                        </button>

                        <h2 className="text-xl font-bold text-slate-800 mb-6">Upload Payroll Data</h2>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Period Selection */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Month</label>
                                    <select
                                        value={uploadMonth}
                                        onChange={(e) => setUploadMonth(e.target.value)}
                                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                                    >
                                        {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Year</label>
                                    <select
                                        value={uploadYear}
                                        onChange={(e) => setUploadYear(e.target.value)}
                                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                                    >
                                        {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                                    </select>
                                </div>
                            </div>

                            {/* File Drop Zone */}
                            <div className="lg:col-span-2">
                                <div
                                    onDragEnter={handleDrag}
                                    onDragLeave={handleDrag}
                                    onDragOver={handleDrag}
                                    onDrop={handleDrop}
                                    onClick={() => fileInputRef.current?.click()}
                                    className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${dragActive
                                        ? 'border-indigo-500 bg-indigo-50'
                                        : selectedFile
                                            ? 'border-green-400 bg-green-50'
                                            : 'border-slate-200 bg-slate-50 hover:border-indigo-400 hover:bg-indigo-50/50'
                                        }`}
                                >
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".xlsx,.xls,.csv"
                                        onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                                        className="hidden"
                                    />

                                    {selectedFile ? (
                                        <div className="flex items-center justify-center gap-3">
                                            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-green-600">
                                                <FileIcon />
                                            </div>
                                            <div className="text-left">
                                                <p className="font-medium text-slate-800">{selectedFile.name}</p>
                                                <p className="text-sm text-slate-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                                            </div>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                                                className="ml-4 p-2 hover:bg-red-100 rounded-lg text-red-500 transition"
                                            >
                                                <CloseIcon />
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center text-indigo-600 mx-auto mb-4">
                                                <UploadIcon />
                                            </div>
                                            <p className="text-slate-700 font-medium mb-1">Drop your Excel/CSV file here</p>
                                            <p className="text-slate-500 text-sm">or click to browse</p>
                                        </>
                                    )}
                                </div>

                                {/* Upload Button & Result */}
                                <div className="mt-4 flex items-center gap-4 flex-wrap">
                                    <button
                                        onClick={handleUpload}
                                        disabled={!selectedFile || uploading}
                                        className={`px-6 py-2.5 rounded-xl font-medium transition flex items-center gap-2 ${selectedFile && !uploading
                                            ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-200'
                                            : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                            }`}
                                    >
                                        {uploading ? (
                                            <>
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                <UploadIcon />
                                                Upload Payroll
                                            </>
                                        )}
                                    </button>

                                    {uploadResult && (
                                        <div className="flex-1">
                                            <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${uploadResult.success && (!uploadResult.errors || uploadResult.errors.length === 0)
                                                ? 'bg-green-50 text-green-700'
                                                : uploadResult.errors && uploadResult.errors.length > 0
                                                    ? 'bg-amber-50 text-amber-700'
                                                    : 'bg-red-50 text-red-700'
                                                }`}>
                                                {uploadResult.success && (!uploadResult.errors || uploadResult.errors.length === 0)
                                                    ? <CheckIcon />
                                                    : <AlertIcon />}
                                                <span className="text-sm font-medium">{uploadResult.message}</span>
                                                {uploadResult.errors && uploadResult.errors.length > 0 && (
                                                    <button
                                                        onClick={() => setShowErrors(!showErrors)}
                                                        className="ml-2 text-xs underline hover:no-underline"
                                                    >
                                                        {showErrors ? 'Hide details' : 'Show details'}
                                                    </button>
                                                )}
                                            </div>

                                            {/* Expanded Error List */}
                                            {showErrors && uploadResult.errors && uploadResult.errors.length > 0 && (
                                                <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-4 max-h-48 overflow-y-auto">
                                                    <p className="text-sm font-bold text-red-800 mb-2">Error Details:</p>
                                                    <ul className="space-y-1">
                                                        {uploadResult.errors.map((err, i) => (
                                                            <li key={i} className="text-sm text-red-700 flex items-start gap-2">
                                                                <span className="text-red-400 mt-0.5">•</span>
                                                                {err}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 pt-6 border-t border-slate-100 flex justify-end">
                            <button
                                onClick={() => setIsUploadModalOpen(false)}
                                className="px-6 py-2.5 rounded-xl font-bold text-slate-600 hover:bg-slate-100 transition"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Summary Cards */}
            {payrollData.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
                        <p className="text-sm text-slate-500 mb-1">Employees</p>
                        <p className="text-2xl font-bold text-slate-800">{payrollData.length}</p>
                    </div>
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-lg shadow-blue-200 p-5 text-white">
                        <p className="text-sm text-blue-100 mb-1">Gross Salary</p>
                        <p className="text-2xl font-bold">{formatCurrency(totals.grossSalary)}</p>
                    </div>
                    <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-2xl shadow-lg shadow-red-200 p-5 text-white">
                        <p className="text-sm text-red-100 mb-1">Total Deductions</p>
                        <p className="text-2xl font-bold">{formatCurrency(totals.totalDeductions)}</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl shadow-lg shadow-green-200 p-5 text-white">
                        <p className="text-sm text-green-100 mb-1">Net Payable</p>
                        <p className="text-2xl font-bold">{formatCurrency(totals.netPay)}</p>
                    </div>
                </div>
            )}

            {/* Period Filter & Data Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-5 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <h2 className="text-lg font-semibold text-slate-800">Payroll Records</h2>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-3">
                            <label className="text-sm text-slate-500">Company:</label>
                            <select
                                value={selectedCompanyId}
                                onChange={(e) => setSelectedCompanyId(e.target.value)}
                                className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all text-sm"
                            >
                                <option value="">All Companies</option>
                                {companies.map(c => (
                                    <option key={c.company_id} value={c.company_id}>{c.company_name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-center gap-3">
                            <label className="text-sm text-slate-500">Period:</label>
                            <select
                                value={selectedPeriodId}
                                onChange={(e) => setSelectedPeriodId(e.target.value)}
                                className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all text-sm"
                            >
                                <option value="">All Periods</option>
                                {periods.map(p => (
                                    <option key={p.id} value={p.id}>{p.month} {p.year}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>


                {loading ? (
                    <div className="flex justify-center p-12">
                        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : payrollData.length === 0 ? (
                    <div className="p-12 text-center">
                        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4 text-slate-400">
                            <FileIcon />
                        </div>
                        <p className="text-slate-500 text-lg font-medium mb-2">No payroll data found</p>
                        <p className="text-slate-400 text-sm">Upload an Excel/CSV file to get started</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse min-w-[1200px]">
                            <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-semibold sticky top-0">
                                <tr>
                                    <th className="p-4">Employee</th>
                                    <th className="p-4 text-center" colSpan={5}>Attendance</th>
                                    <th className="p-4 text-center" colSpan={3}>Earnings</th>
                                    <th className="p-4 text-center" colSpan={3}>Net Pay</th>
                                    <th className="p-4">Action</th>
                                </tr>
                                <tr className="bg-slate-25 border-b border-slate-100">
                                    <th className="p-3 font-medium"></th>
                                    <th className="p-3 font-medium text-center">TD</th>
                                    <th className="p-3 font-medium text-center">PD</th>
                                    <th className="p-3 font-medium text-center">WH</th>
                                    <th className="p-3 font-medium text-center">OT/Arr</th>
                                    <th className="p-3 font-medium text-center">SD</th>
                                    <th className="p-3 font-medium text-center">Basic</th>
                                    <th className="p-3 font-medium text-center">Gross</th>
                                    <th className="p-3 font-medium text-center">Deduct</th>
                                    <th className="p-3 font-medium text-center">Net Sal</th>
                                    <th className="p-3 font-medium text-center">Ret 5%</th>
                                    <th className="p-3 font-medium text-center">Net Pay</th>
                                    <th className="p-3 font-medium"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {payrollData.map((row) => (
                                    <tr key={`${row.employee_id}-${row.period_id}`} className="hover:bg-slate-50/50 transition">
                                        <td className="p-4">
                                            <div className="font-medium text-slate-900">{row.employee_name}</div>
                                            <div className="text-xs text-slate-500">{row.employee_id} • {row.company} • {row.department}</div>
                                        </td>

                                        <td className="p-3 text-center text-sm">{row.total_days || '-'}</td>
                                        <td className="p-3 text-center text-sm">{row.paid_days || '-'}</td>
                                        <td className="p-3 text-center text-sm">{row.weekly_holidays || '-'}</td>
                                        <td className="p-3 text-center text-sm">{row.ot_arrears_days || '-'}</td>
                                        <td className="p-3 text-center text-sm font-medium text-indigo-600">{row.salary_days || '-'}</td>
                                        <td className="p-3 text-center text-sm">{formatCurrency(row.earned_basic)}</td>
                                        <td className="p-3 text-center text-sm font-medium text-blue-600">{formatCurrency(row.gross_salary)}</td>
                                        <td className="p-3 text-center text-sm text-red-600">{formatCurrency(row.total_deductions)}</td>
                                        <td className="p-3 text-center text-sm">{formatCurrency(row.net_salary)}</td>
                                        <td className="p-3 text-center text-sm text-orange-600">{formatCurrency(row.retention_amount)}</td>
                                        <td className="p-3 text-center text-sm font-bold text-green-600">{formatCurrency(row.net_pay)}</td>
                                        <td className="p-3">
                                            <button
                                                onClick={() => setSelectedEmployee(row)}
                                                className="p-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition"
                                            >
                                                <EyeIcon />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Employee Detail Modal */}
            {selectedEmployee && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl max-h-[90vh] overflow-y-auto">
                        <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center sticky top-0 bg-white">
                            <div>
                                <h2 className="text-xl font-bold text-slate-800">{selectedEmployee.employee_name}</h2>
                                <p className="text-sm text-slate-500">{selectedEmployee.employee_id} • {selectedEmployee.period}</p>
                            </div>
                            <button onClick={() => setSelectedEmployee(null)} className="p-2 hover:bg-slate-100 rounded-full transition">
                                <CloseIcon />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Attendance */}
                            <div>
                                <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <span className="w-6 h-6 bg-indigo-100 text-indigo-600 rounded flex items-center justify-center text-xs">1</span>
                                    Attendance
                                </h3>
                                <div className="grid grid-cols-5 gap-4">
                                    {[
                                        { label: 'Total Days', value: selectedEmployee.total_days },
                                        { label: 'Paid Days', value: selectedEmployee.paid_days },
                                        { label: 'Weekly Holidays', value: selectedEmployee.weekly_holidays },
                                        { label: 'OT/Arrears', value: selectedEmployee.ot_arrears_days },
                                        { label: 'Salary Days', value: selectedEmployee.salary_days, highlight: true },
                                    ].map((item, i) => (
                                        <div key={i} className={`p-4 rounded-xl text-center ${item.highlight ? 'bg-indigo-50 border border-indigo-100' : 'bg-slate-50'}`}>
                                            <p className="text-xs text-slate-500 mb-1">{item.label}</p>
                                            <p className={`text-xl font-bold ${item.highlight ? 'text-indigo-600' : 'text-slate-800'}`}>{item.value || 0}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Earnings */}
                            <div>
                                <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded flex items-center justify-center text-xs">2</span>
                                    Earnings
                                </h3>
                                <div className="bg-slate-50 rounded-xl p-4">
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                        {[
                                            { label: 'Actual Basic', value: selectedEmployee.actual_basic },
                                            { label: 'Earned Basic', value: selectedEmployee.earned_basic },
                                            { label: 'HRA', value: selectedEmployee.earned_hra },
                                            { label: 'Conveyance', value: selectedEmployee.earned_conveyance },
                                            { label: 'Medical', value: selectedEmployee.earned_medical },
                                            { label: 'Special Allowance', value: selectedEmployee.earned_special_allowance },
                                            { label: 'OT/Arrears Amount', value: selectedEmployee.ot_arrears_amount },
                                        ].map((item, i) => (
                                            <div key={i} className="flex justify-between">
                                                <span className="text-sm text-slate-600">{item.label}</span>
                                                <span className="text-sm font-medium text-slate-800">{formatCurrency(item.value)}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between">
                                        <span className="font-medium text-slate-700">Gross Salary</span>
                                        <span className="text-lg font-bold text-blue-600">{formatCurrency(selectedEmployee.gross_salary)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Deductions */}
                            <div>
                                <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <span className="w-6 h-6 bg-red-100 text-red-600 rounded flex items-center justify-center text-xs">3</span>
                                    Deductions
                                </h3>
                                <div className="bg-slate-50 rounded-xl p-4">
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                        {[
                                            { label: 'EPF', value: selectedEmployee.epf },
                                            { label: 'Professional Tax', value: selectedEmployee.pt },
                                            { label: 'ESI', value: selectedEmployee.esi },
                                            { label: 'LWF', value: selectedEmployee.lwf },
                                            { label: 'Advance', value: selectedEmployee.advance },
                                            { label: 'Canteen', value: selectedEmployee.canteen },
                                        ].map((item, i) => (
                                            <div key={i} className="flex justify-between">
                                                <span className="text-sm text-slate-600">{item.label}</span>
                                                <span className="text-sm font-medium text-red-600">{formatCurrency(item.value)}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between">
                                        <span className="font-medium text-slate-700">Total Deductions</span>
                                        <span className="text-lg font-bold text-red-600">{formatCurrency(selectedEmployee.total_deductions)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Net Pay */}
                            <div>
                                <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <span className="w-6 h-6 bg-green-100 text-green-600 rounded flex items-center justify-center text-xs">4</span>
                                    Net Pay Calculation
                                </h3>
                                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-100 rounded-xl p-4 space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-slate-600">Net Salary (Gross - Deductions)</span>
                                        <span className="font-medium">{formatCurrency(selectedEmployee.net_salary)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-600">Retention (5%)</span>
                                        <span className="font-medium text-orange-600">- {formatCurrency(selectedEmployee.retention_amount)}</span>
                                    </div>
                                    <div className="pt-3 border-t border-green-200 flex justify-between items-center">
                                        <span className="text-lg font-semibold text-slate-700">Final Net Pay</span>
                                        <span className="text-2xl font-bold text-green-600">{formatCurrency(selectedEmployee.net_pay)}</span>
                                    </div>
                                </div>
                            </div>

                            {selectedEmployee.remarks && (
                                <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                                    <p className="text-sm font-medium text-amber-800">Remarks</p>
                                    <p className="text-amber-700">{selectedEmployee.remarks}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
